import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { STATUS_TONE, getStatusTone, StatusTone } from '../theme/tokens';

/**
 * Canonical status chip.
 *
 * One look, one shape, one source of truth — used everywhere a workflow
 * state is rendered (PO, Bill, Statement, inline panels). The colour
 * pair is resolved through `getStatusTone()` in `theme/tokens.ts`, so
 * every chip in the app re-skins from a single token edit.
 *
 * Usage:
 *
 *   <StatusChip status={po.status} />              // default density
 *   <StatusChip status="Approved" dense />         // h=18, .6rem — table cells
 *   <StatusChip status="Draft" tone="neutral" />   // explicit tone override
 *   <StatusChip status="Bill Received" onClick={…} // also accepts MUI Chip props
 *               deleteIcon={<ArrowDropDownIcon/>}
 *               onDelete={…} />                    // for ▾-override pickers
 *
 * NEVER use raw hex / colour='primary' for a workflow chip — always
 * route through StatusChip so PMs see one consistent palette.
 */

export interface StatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
  /** Status text rendered inside the chip. Tone is auto-resolved. */
  status: string;
  /** Override the auto-resolved tone (rare — use only for unknown statuses). */
  tone?: StatusTone;
  /** Compact density — for inside dense tables. */
  dense?: boolean;
  /** Replace the rendered label without changing the tone source. */
  label?: React.ReactNode;
}

export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  tone,
  dense = false,
  label,
  sx,
  ...rest
}) => {
  const t = STATUS_TONE[tone ?? getStatusTone(status)];
  const height = dense ? 18 : 20;
  const fontSize = dense ? '0.6rem' : '0.65rem';

  return (
    <Chip
      label={label ?? status}
      size="small"
      sx={{
        height,
        fontSize,
        fontWeight: 700,
        borderRadius: 999,
        backgroundColor: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        '& .MuiChip-label':      { px: dense ? 0.7 : 0.9 },
        '& .MuiChip-icon':       { color: `${t.fg} !important`, fontSize: dense ? 12 : 14, ml: 0.4 },
        '& .MuiChip-deleteIcon': { color: `${t.fg} !important`, '&:hover': { color: `${t.fg} !important` } },
        ...sx,
      }}
      {...rest}
    />
  );
};

export default StatusChip;
